import type { SettingsListActionRef } from "../../../components/SettingsListPage";
import type { FormInstance } from "../../../../../../../shared/frontend/ui";
import { Form } from "../../../../../../../shared/frontend/ui";
import dayjs from "dayjs";
import React from "react";
import type { PersonDto } from "../types";
import type { WalletHistoryRow } from "./PersonWalletHistorySection";
import type { ApiWalletDto,WalletCreateFormValues,WalletProductDto,WalletRow } from "./personDetailTypes";
import { ISO_DATE_LENGTH } from "../../../app/utils/isoDate";

type WalletApiClient = {
  get<T>(url: string, opts?: { suppressGlobalErrorToast?: boolean }): Promise<T>;
};

type UsePersonWalletDrawerArgs = {
  api: WalletApiClient;
  walletProducts: WalletProductDto[];
  walletsRef: React.RefObject<SettingsListActionRef | null>;
  contractsRef: React.RefObject<SettingsListActionRef | null>;
  setPerson: (person: PersonDto) => void;
};

type UsePersonWalletDrawerResult = {
  open: boolean;
  saving: boolean;
  setSaving: (saving: boolean) => void;
  form: FormInstance<WalletCreateFormValues>;
  balanceTouched: boolean;
  setBalanceTouched: (touched: boolean) => void;
  edit: WalletRow | null;
  isLocked: boolean;
  setIsLocked: (isLocked: boolean) => void;
  adjustAmount: number | null;
  setAdjustAmount: (amount: number | null) => void;
  history: WalletHistoryRow[];
  historyLoading: boolean;
  selectedWalletProduct: WalletProductDto | null;
  setSelectedWalletProductId: (walletProductId: string) => void;
  openCreate: () => void;
  openEdit: (wallet: WalletRow) => void;
  close: () => void;
  handleDeleted: () => void;
  handleSaved: (updatedPerson: PersonDto | null) => void;
  refetchWalletAndHistory: () => Promise<void>;
};

export function usePersonWalletDrawer({
  api,
  walletProducts,
  walletsRef,
  contractsRef,
  setPerson
}: UsePersonWalletDrawerArgs): UsePersonWalletDrawerResult {
  const [open, setOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [form] = Form.useForm<WalletCreateFormValues>();
  const [balanceTouched, setBalanceTouched] = React.useState(false);
  const [edit, setEdit] = React.useState<WalletRow | null>(null);
  const [isLocked, setIsLocked] = React.useState(false);
  const [adjustAmount, setAdjustAmount] = React.useState<number | null>(null);
  const [history, setHistory] = React.useState<WalletHistoryRow[]>([]);
  const [historyLoading, setHistoryLoading] = React.useState(false);
  const [selectedWalletProductId, setSelectedWalletProductId] = React.useState("");

  React.useEffect(() => {
    if (!edit?.id) {
      setHistory([]);
      setHistoryLoading(false);
      return;
    }

    let alive = true;
    setHistoryLoading(true);
    api
      .get<WalletHistoryRow[]>(`/api/v1/wallets/${encodeURIComponent(String(edit.id))}/history`)
      .then((list) => {
        if (!alive) return;
        setHistory(list || []);
        setHistoryLoading(false);
      })
      .catch(() => {
        if (!alive) return;
        setHistoryLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [api, edit?.id]);

  const selectedWalletProduct = React.useMemo(() => {
    const id = edit?.walletProductId
      ? String(edit.walletProductId)
      : selectedWalletProductId;
    if (!id) return null;
    return walletProducts.find((w) => String(w.id) === id) || null;
  }, [edit, selectedWalletProductId, walletProducts]);

  const close = React.useCallback(() => {
    setOpen(false);
    setEdit(null);
    setIsLocked(false);
    setAdjustAmount(null);
    setSelectedWalletProductId("");
  }, []);

  const openEdit = React.useCallback(
    (wallet: WalletRow) => {
      const row = wallet;
      setEdit(wallet);
      setSelectedWalletProductId(String(row.walletProductId ?? ""));
      setBalanceTouched(false);
      form.setFieldsValue({
        walletProductId: row.walletProductId ?? undefined,
        balance: row.balance ?? 0,
        usageLimitValue: row.usageLimitValue ?? null,
        usageLimitUnit: row.usageLimitUnit ?? null,
        validFrom: row.validFrom ? dayjs(String(row.validFrom).slice(0, ISO_DATE_LENGTH)) : dayjs(),
        validTo: row.validTo ? dayjs(String(row.validTo).slice(0, ISO_DATE_LENGTH)) : null,
        allowsAccess: row.allowsAccess !== false
      });
      setIsLocked(row.isManuallyLocked === true || (row.isManuallyLocked == null && row.isActive === false));
      setOpen(true);
    },
    [form]
  );

  const openCreate = React.useCallback(() => {
    setEdit(null);
    setSelectedWalletProductId("");
    setBalanceTouched(false);
    setIsLocked(false);
    setAdjustAmount(null);
    form.resetFields();
    form.setFieldsValue({ balance: 0, validFrom: dayjs(), validTo: null, allowsAccess: false, paymentMethod: null });
    setOpen(true);
  }, [form]);

  const handleDeleted = React.useCallback(() => {
    setOpen(false);
    setEdit(null);
    setAdjustAmount(null);
    setSelectedWalletProductId("");
    walletsRef.current?.reload?.();
    contractsRef.current?.reload?.();
  }, [contractsRef, walletsRef]);

  const handleSaved = React.useCallback(
    (updatedPerson: PersonDto | null) => {
      if (updatedPerson) setPerson(updatedPerson);
      setOpen(false);
      setEdit(null);
      setIsLocked(false);
      setAdjustAmount(null);
      setSelectedWalletProductId("");
      walletsRef.current?.reload?.();
    },
    [setPerson, walletsRef]
  );

  const refetchWalletAndHistory = React.useCallback(async () => {
    if (!edit?.id) return;
    {
      const updated = await api.get<WalletRow & { walletTypeSnapshot?: number }>(`/api/v1/wallets/${encodeURIComponent(String(edit.id))}`);
      const updatedWallet = updated as ApiWalletDto;
      const balance = Number(updatedWallet.balance ?? edit.balance);
      setEdit((prev) => prev ? { ...prev, balance, walletType: updatedWallet.walletTypeSnapshot ?? updatedWallet.walletType ?? prev.walletType } : null);
      form.setFieldValue("balance", balance);
      const list = await api
        .get<WalletHistoryRow[]>(`/api/v1/wallets/${encodeURIComponent(String(edit.id))}/history`);
      setHistory(list || []);
    }
  }, [api, edit, form]);

  return {
    open,
    saving,
    setSaving,
    form,
    balanceTouched,
    setBalanceTouched,
    edit,
    isLocked,
    setIsLocked,
    adjustAmount,
    setAdjustAmount,
    history,
    historyLoading,
    selectedWalletProduct,
    setSelectedWalletProductId,
    openCreate,
    openEdit,
    close,
    handleDeleted,
    handleSaved,
    refetchWalletAndHistory
  };
}
